/**
 * Phase 3b §2 — Verbatim narrative copy (source of truth).
 *
 * Canonical prose for Charles's story + chronic care reveals. Matches
 * `/app/memory/NARRATIVE_COPY.md` byte-for-byte. Do NOT paraphrase,
 * compress, or reformat — these strings ship verbatim to the reader.
 *
 * To preserve paragraph integrity while keeping inline emphasis
 * controllable from JSX, each piece is modeled as an ordered list of
 * segments, where a segment is either a plain paragraph string OR
 * a small object describing inline emphasis. The renderer in
 * `components/employee/CharlesStory.jsx` walks the list and applies
 * italics/bold/coral per the segment type.
 */

/* ------------------------------------------------------------------- */
/* Feature flag: is chronic care a built-in benefit (current ship) or  */
/* an elected rider (pending MassMutual confirmation)?                 */
/*                                                                     */
/* Default: false = built-in. Flip to true once MassMutual confirms    */
/* the benefit is an elected rider; the one affected paragraph in      */
/* each chronic-care version swaps to its rider-case variant.          */
/* ------------------------------------------------------------------- */
export const NARRATIVE_CHRONIC_CARE_IS_RIDER = false;


/* =========================================================================
   CHARLES'S STORY
   Rendered VERBATIM. Every paragraph is reproduced — no summarization.
   The "beat" line ("Five employers. One policy. Same rate.") is the ONLY
   line italicized. The final takeaway renders bold + coral-accent.
   ========================================================================= */
export const CHARLES_STORY = {
    // Ordered paragraph list. Each item is either:
    //   { type: "p",       text: "..." }                  → plain paragraph
    //   { type: "beat",    text: "..." }                  → italic beat line
    //   { type: "takeaway", lines: ["...", "..."] }       → bold coral closer
    segments: [
        {
            type: "p",
            text: "Charles is 62 now. He signed up for Laguna when he was 38, back when he worked at a printing company in Cincinnati. He paid about $41 every two weeks for $75,000 of coverage \u2014 the same $41 he still pays today, twenty-four years later.",
        },
        {
            type: "p",
            text: "He left the printing company after nine years to start something of his own. Then he sold that and joined a software firm. Then he got laid off during a merger. Then he freelanced. Then he took a job at a logistics company that he stayed at until he retired last spring.",
        },
        {
            type: "beat",
            text: "Five employers. One policy. Same rate.",
        },
        {
            type: "p",
            text: "If he'd bought whole life from an agent the year he left the printing company, at 47 it would have cost him about $112 every two weeks for the same coverage. If he'd waited until the layoff at 51, it would have been closer to $148. And that's assuming he passed underwriting each time \u2014 which got harder after his knee surgery at 45.",
        },
        {
            type: "p",
            text: "Charles didn't have to think about any of that. He enrolled once, at 38, through his employer. The policy followed him through everything after.",
        },
        {
            // The leading prose stays regular; the final two sentences render
            // bold + coral-accent on their own, visually separated.
            type: "takeaway",
            leadin: "That's the part that's easy to miss when you're looking at the premium on day one.",
            lines: [
                "The premium today is the premium forever.",
                "And the policy is yours from the moment you sign.",
            ],
        },
    ],
    footnote: "Charles's story is composed from real customer experiences.",
};


/* =========================================================================
   CHRONIC CARE — Version A (employer-facing)
   Not yet placed in the current build. Exported so any future employer-
   reinforcement modal / proposal "what it covers" panel can render it
   without duplicating the copy.
   ========================================================================= */
const _CHRONIC_A_BUILTIN_PARAGRAPH =
    "This is built into the base policy. No separate rider, no extra premium, no special election. Every person your team enrolls has access to it.";

const _CHRONIC_A_RIDER_PARAGRAPH =
    "This is an option they can add to their coverage. No extra medical questions, no separate approval process \u2014 employees can elect it during their application, and it becomes part of their policy from day one for a small additional premium.";

export const CHRONIC_CARE_V1_TEXT = {
    hook: "It protects them while they're still alive.",
    paragraphs: [
        "Most life insurance only helps a family after the person they loved is gone. This coverage does something different.",
        "If any of your employees ever becomes chronically ill \u2014 unable to perform basic daily activities on their own, or facing severe cognitive decline \u2014 a portion of their death benefit becomes available to them during their lifetime, to help pay for the care they need.",
        "Home health care. Nursing facility. An aide who comes to their apartment so their family doesn't have to stop working to take care of them. Whatever helps them keep their dignity during the hardest stretch of their life.",
        NARRATIVE_CHRONIC_CARE_IS_RIDER ? _CHRONIC_A_RIDER_PARAGRAPH : _CHRONIC_A_BUILTIN_PARAGRAPH,
        "Your employees won't find this in the invitation email. It'll surface during their application as an unexpected gift \u2014 which is exactly the moment it lands hardest.",
    ],
    // Bold emphasis on the fourth paragraph's leading sentence (handled by
    // the renderer — kept here for authoring clarity).
    emphasisedParagraphIndex: 3,
};


/* =========================================================================
   CHRONIC CARE — Version B (employee-facing)
   Rendered in PortalEmployee.jsx phase 1 Section E.
   ========================================================================= */
const _CHRONIC_B_BUILTIN_PARAGRAPH =
    "It's part of your policy. No extra cost. No separate paperwork. No waiting period once you're covered.";

const _CHRONIC_B_RIDER_PARAGRAPH =
    "You can add this to your coverage. No extra medical questions, no waiting period \u2014 just a small additional premium that makes the chronic-illness protection part of your policy from the day it's issued.";

export const CHRONIC_CARE_V2_TEXT = {
    hook: "One more thing, before you finish.",
    paragraphs: [
        "Most life insurance only helps your family after you're gone. This one is different.",
        // Note: the inline italic on "you" is emotionally load-bearing; the
        // renderer splits this paragraph on the `*you*` marker and wraps
        // that token in <em>.
        "If you ever become chronically ill \u2014 unable to bathe, dress, or move on your own, or facing severe memory loss \u2014 a portion of your coverage becomes available to *you*, while you're alive, to help pay for care.",
        "Home health. A nursing facility. An aide who comes to your apartment so your daughter doesn't have to stop working to take care of you. Whatever helps you keep your dignity during the hardest stretch of your life.",
        NARRATIVE_CHRONIC_CARE_IS_RIDER ? _CHRONIC_B_RIDER_PARAGRAPH : _CHRONIC_B_BUILTIN_PARAGRAPH,
    ],
    emphasisedParagraphIndex: 3,
    closing: "Your employer didn't have to give you this. They did anyway.",
};


/* =========================================================================
   Rider-case exports — verbatim swap paragraphs for downstream test access.
   Not imported by the runtime; exported so tests can assert verbatim match.
   ========================================================================= */
export const CHRONIC_CARE_V1_RIDER_SWAP_PARAGRAPH = _CHRONIC_A_RIDER_PARAGRAPH;
export const CHRONIC_CARE_V2_RIDER_SWAP_PARAGRAPH = _CHRONIC_B_RIDER_PARAGRAPH;
